import React, { useMemo, useRef, useState } from 'react';
import { LogOut, Moon, Search, Settings, Sun, User, Building2, ArrowLeft, Check, ChevronDown, Eye } from 'lucide-react';

import { PulseDot } from '@/components/layout/data-pulse-chip';
import { AlertsBell } from '@/components/layout/alerts-bell';
import { AskBar } from '@/components/layout/ask-bar';
import { useQbee } from '@/components/qbee/qbee-context';
import { DateWindow } from '@/components/commerce/date-window';
import { useWindowBar, DEFAULT_DAYS } from '@/providers/report-window';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { inMemberSession, currentWorkspaceName, fetchMemberWorkspaces, enterWorkspace, endMemberSession, backToConsole } from '@/lib/agencyMember';
import { platformToken, fetchPlatformWorkspaces, switchAsSuperadmin, switchFailure } from '@/lib/platform-session';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn, initials, titleCase } from '@/lib/utils';
import { useTheme } from '@/providers/theme-provider';
import { useNavigate } from 'react-router-dom';
import { isReadOnly, getUserRole } from '@/lib/permissions';
import { ROLES } from '@/lib/permissions';
import { workspaceDay } from '@/lib/workspace-display';

/**
 * THE ONE WORKSPACE CONTROL. The pill that names the workspace is also the way
 * out of it: current workspace, the switcher (agency members), and Back to the
 * Agency Console. It replaced two outline buttons at the right end of the bar
 * ("Agency Console", "Switch workspace") that crowded the search off centre
 * and read, beside four status pills, as a bar with too much in it.
 *
 * A Popover rather than a DropdownMenu, because the list has a search box and
 * a menu's typeahead steals the keys typed into it. Every row is a button, so
 * Tab and Enter work, and Escape closes it.
 */
function WorkspaceMenu({ name, imp = false, orgId = null }) {
  const member = inMemberSession();
  // A superadmin session (the console's impersonation token) switches too,
  // through the console's own audited endpoint: lib/platform-session.
  const superadmin = !member && Boolean(imp) && Boolean(platformToken());
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['member-workspaces'],
    queryFn: fetchMemberWorkspaces,
    enabled: member,
    retry: false,
    staleTime: 60_000,
  });
  const sa = useQuery({
    queryKey: ['platform-workspaces'],
    queryFn: fetchPlatformWorkspaces,
    enabled: superadmin && open,
    retry: false,
    staleTime: 60_000,
  });
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const switcher = member || superadmin;
  const workspaces = useMemo(() => (member ? data?.workspaces : sa.data) || [], [member, data, sa.data]);
  const loaded = member ? Boolean(data) : Boolean(sa.data);
  const current = name || '';
  const needle = q.trim().toLowerCase();
  const list = workspaces.filter((w) => !needle || String(w.name || '').toLowerCase().includes(needle));
  const searchable = workspaces.length > 6;
  if (!current && !member) return null;
  const open_ = async (w) => {
    setBusy(w.org_id);
    setFailed(null);
    try {
      if (member) await enterWorkspace(w.org_id, w.name);
      else await switchAsSuperadmin(w.org_id);
    } catch (e) {
      setBusy(null);
      if (superadmin) setFailed(switchFailure(e));
    }
  };

  const row = 'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13.5px] outline-none transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-ring';
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setQ(''); setFailed(null); } }}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={`Workspace: ${current || 'choose'}. Open the workspace menu`}
          className={cn('inline-flex h-9 min-w-0 max-w-[260px] items-center gap-1.5 rounded-full border border-border bg-surface-subtle px-2.5 outline-none sm:pr-2 transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring',
            open && 'bg-accent')}>
          <Building2 className="size-4 shrink-0 text-muted-foreground" />
          <span className="hidden truncate text-[13px] font-semibold tracking-tight sm:inline">{current || 'Workspaces'}</span>
          <ChevronDown className={cn('hidden size-3.5 shrink-0 text-muted-foreground transition-transform sm:block', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(300px,calc(100vw-24px))] p-1.5"
        onOpenAutoFocus={(e) => { if (searchable) { e.preventDefault(); searchRef.current?.focus(); } }}>
        {current && (
          <div className="px-2.5 pb-2 pt-1.5">
            <p className="text-[12.5px] text-muted-foreground">You are in</p>
            <p className="truncate text-[14px] font-semibold" title={current}>{current}</p>
          </div>
        )}
        {switcher && (
          <>
            <div className="border-t border-border pt-1.5">
              <p className="px-2.5 text-[13px] font-medium text-muted-foreground">Switch workspace</p>
              {superadmin && <p className="px-2.5 pb-1 text-[12.5px] text-muted-foreground/80">Opens as that workspace&apos;s admin · logged for audit</p>}
              {!superadmin && <div className="pb-1" />}
              {searchable && (
                <div className="relative px-1 pb-1.5">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 size-3.5 -translate-y-[65%] text-muted-foreground" />
                  <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a workspace"
                    aria-label="Find a workspace"
                    className="h-8 w-full rounded-md border border-border bg-background pl-7 pr-2 text-[13.5px] outline-none focus:border-primary" />
                </div>
              )}
              {failed && <p role="alert" className="mx-1 mb-1.5 rounded-md bg-rose-500/10 px-2.5 py-1.5 text-[12.5px] text-rose-700 dark:text-rose-400">{failed}</p>}
              <div className="max-h-[min(50vh,320px)] overflow-y-auto">
                {workspaces.length === 0 && (
                  <p className="px-2.5 py-1.5 text-[13px] text-muted-foreground">
                    {superadmin && sa.isError ? switchFailure(sa.error) : loaded ? 'No workspaces assigned.' : 'Loading the workspaces...'}
                  </p>
                )}
                {workspaces.length > 0 && list.length === 0 && <p className="px-2.5 py-1.5 text-[13px] text-muted-foreground">None match.</p>}
                {list.map((w) => {
                  const here = (orgId != null && String(w.org_id) === String(orgId)) || w.name === current;
                  return (
                    <button key={w.org_id} type="button" className={row} disabled={busy != null}
                      aria-current={here ? 'true' : undefined}
                      onClick={() => { if (here) { setOpen(false); return; } open_(w); }}>
                      <span className="grid size-4 shrink-0 place-items-center">{here && <Check className="size-4 text-primary" />}</span>
                      <span className="min-w-0 flex-1 truncate">{w.name}</span>
                      {busy === w.org_id
                        ? <span className="text-[12.5px] text-muted-foreground">Opening...</span>
                        : w.status && w.status !== 'active' && <span className="shrink-0 text-[12px] text-muted-foreground first-letter:uppercase">{w.status}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-1 border-t border-border pt-1">
              {member ? (
                <>
                  <button type="button" className={row} onClick={() => backToConsole()}>
                    <ArrowLeft className="size-4 shrink-0 text-muted-foreground" /> Back to Agency Console
                  </button>
                  <button type="button" className={row} onClick={() => endMemberSession()}>
                    <LogOut className="size-4 shrink-0 text-muted-foreground" /> Sign out of the agency
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className={row} onClick={() => { setOpen(false); navigate('/settings?tab=general'); }}>
                    <Settings className="size-4 shrink-0 text-muted-foreground" /> Workspace settings
                  </button>
                  <button type="button" className={row} onClick={() => { window.location.assign('/app/superadmin'); }}>
                    <ArrowLeft className="size-4 shrink-0 text-muted-foreground" /> Back to the console
                  </button>
                </>
              )}
            </div>
          </>
        )}
        {/* A superadmin session whose console sign-in is not in this browser
            cannot switch from here; it says where to. */}
        {!switcher && imp && (
          <div className="border-t border-border pt-1">
            <button type="button" className={row} onClick={() => { window.location.assign('/app/superadmin'); }}>
              <ArrowLeft className="size-4 shrink-0 text-muted-foreground" /> Switch from the console
            </button>
          </div>
        )}
        {!switcher && (
          <div className="border-t border-border pt-1">
            <button type="button" className={row} onClick={() => { setOpen(false); navigate('/settings?tab=general'); }}>
              <Settings className="size-4 shrink-0 text-muted-foreground" /> Workspace settings
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The reporting window, in the one bar that never scrolls.
 *
 * It sits at the TOP RIGHT, which is where GA4, Shopify's own analytics and
 * every other reporting tool a client already uses put it - so nobody has to
 * learn where ours lives. It was on the left of search, and a client asked for
 * this by name; the reports had been saying "the window picked top-right" all
 * along, so the copy was right and the control was not.
 *
 * It renders only while a report page is mounted - a window control above
 * Settings would imply a filter that does not exist - and it says the window a
 * page can actually answer, not the one that was asked for, whenever the two
 * differ.
 */
function WindowChip() {
  const w = useWindowBar();
  if (!w || !w.reg) return null;
  const { min, max } = w.reg;
  // 'all' has no day count, so the clamp note below has nothing to say about it.
  //
  // And a page that never claimed it can answer All time reads its own default
  // instead - so the chip must show that default too. One window is shared
  // across the product, and a picker saying "All time" above a page that used
  // 28 days is the same lie as a badge saying first-party over a subdomain.
  const canAll = !!w.reg.allowAll;
  const isAll = w.days === 'all' && !w.range && canAll;
  const shownDays = (w.days === 'all' && !canAll) ? DEFAULT_DAYS : w.days;
  const asked = w.range
    ? Math.max(1, Math.round((new Date(`${w.range.to}T00:00:00`) - new Date(`${w.range.from}T00:00:00`)) / 86400000) + 1)
    : shownDays;
  let used = asked;
  if (!isAll && min != null) used = Math.max(min, used);
  if (!isAll && max != null) used = Math.min(max, used);
  // A page that can only read "the last N days" (trailingOnly) answers a
  // carried-over range like 1-27 Sep as the last 27 days. The picker kept
  // saying "Custom 1 Sept - 27 Sept" above a page showing 12 Sep - 8 Oct
  // (Command Center, 8 Oct 2026); it now shows what the page is answering.
  // The workspace's today: a range is compared with the day the server cut.
  const todayKey = workspaceDay(new Date());
  const readAsTrailing = !!w.reg.trailingOnly && !!w.range && w.range.to !== todayKey;

  return (
    <div className="flex shrink-0 items-center gap-2">
      {/* How current the data is, INSIDE the window that selects it: the two
          questions ("which days" and "are those days complete") are asked in
          the same breath. It was a pill of its own; now it is the dot at the
          left of the picker, with the same popover behind it. */}
      <DateWindow lead={<PulseDot />} days={readAsTrailing ? used : shownDays} range={readAsTrailing ? null : w.range} onDays={w.setDays} onRange={w.setRange}
        upto={w.upto} endsToday={!!w.reg.live}
        trailingOnly={!!w.reg.trailingOnly}
        allowAll={!!w.reg.allowAll}
        compare={w.reg.compare ? !!w.reg.compare.on : undefined}
        onCompare={w.reg.compare ? w.reg.compare.set : undefined}
        compareNote={w.reg.compare?.note} />
      {readAsTrailing && (
        <span className="hidden items-center rounded-md bg-amber-500/10 px-1.5 py-0.5 text-[13px] font-semibold text-amber-700 lg:inline-flex dark:text-amber-400"
          title={`This report measures a window ending today, so your range ${w.range.from} to ${w.range.to} is read as the last ${used} days.`}>
          ends today
        </span>
      )}
      {!isAll && !readAsTrailing && used !== asked && (
        <span className="hidden items-center rounded-md bg-amber-500/10 px-1.5 py-0.5 text-[13px] font-semibold text-amber-700 lg:inline-flex dark:text-amber-400"
          title={`This report holds ${min != null && used === min ? `no less than ${min}` : `no more than ${max}`} days, so it is answering over ${used} days rather than ${asked}.`}>
          showing {used}d
        </span>
      )}
    </div>
  );
}

export function Topbar({ user, onLogout, onOpenSearch, pageTitle, action }) {
  // The full Ask box (and its search) gives way to icons while Qbee is open.
  const qbee = useQbee();
  // WHICH WORKSPACE AM I IN - asked by everyone, not only agency members.
  //
  // This used to be gated on an agency session, on the reasoning that a tenant
  // in their own workspace has nothing to disambiguate. That left the whole
  // left third of the bar empty for them, and on a page that registers no
  // reporting window (an order, a customer - anything showing ONE record) the
  // header had nothing in it at all but the avatar. A bar with one thing in it
  // reads as broken, and people reported it as the header disappearing.
  //
  // Read from /me rather than the cached name: renaming a workspace in Settings
  // used to leave the stale one on screen until you switched workspaces and
  // back. Same query key as the shell's banner, so this shares that one fetch.
  const { data: meWs } = useQuery({
    queryKey: ['auth', 'me-shell'],
    queryFn: async () => (await api.get('/api/auth/me')).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const workspaceName = meWs?.org_name || (inMemberSession() ? currentWorkspaceName() : null);
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const readOnly = isReadOnly();
  const roleLabel = ROLES[getUserRole()]?.label || null;

  // White chrome on a 98% canvas is a 2-in-255 step, so the bottom edge is the
  // only thing saying "this is a bar". It was border-light at a sub-pixel
  // hairline, which on a 4K screen is not there at all - the search box and the
  // account controls read as items floating on the page. A full-weight border
  // plus the soft shadow gives the header a real edge at any density.
  /* Sides take what they need, the search takes what is left.
   *
   * Equal 1fr sides put the search in the dead centre of the bar, which is only
   * safe while both sides are narrower than a third. Dead centre is not worth a
   * collision: the middle track absorbs the remainder and the search centres
   * inside THAT, so it sits between the two groups and can never reach either.
   *
   * WHAT IS IN IT (28 Sep 2026, the owner: "too many pills on the menu bar"):
   *   workspace menu | Ask | . . . | Alerts bell | date picker (with the
   *   freshness dot) | avatar.
   * Every status pill (sending off, the first-party verdict, spend behind,
   * tracking lost) is a row under the bell; the agency buttons are inside
   * the workspace menu; the theme toggle and read-only are in the avatar menu.
   * A new "look at this" goes in the bell's registry, never in the bar. */
  return (
    <header className="sticky top-0 z-30 grid h-14 grid-cols-[minmax(0,auto)_minmax(0,1fr)_auto] items-center gap-2 overflow-hidden bg-sidebar px-3 sm:gap-4 sm:px-6">
      {/* Left - WHICH WORKSPACE YOU ARE STANDING IN, and the way to another.
          Every number on the screen belongs to this store, and the bar is
          what spans them all. */}
      <div className="flex min-w-0 items-center gap-3">
        <WorkspaceMenu name={workspaceName} imp={Boolean(meWs?.imp)} orgId={meWs?.org_id ?? null} />
        {pageTitle && (
          <h1 className="truncate text-base font-semibold tracking-tight">
            {pageTitle}
          </h1>
        )}
      </div>

      {/* Centre - Ask Cookiebees, with the global search (Cmd+K) at the end
          of the same box. Centred in the middle track so it can never run
          into either side. */}
      <div className="flex min-w-0 justify-center">
        <AskBar onOpenSearch={onOpenSearch} />
      </div>

      {/* Right - what needs you, the reporting window, you. */}
      <div className="flex min-w-0 items-center justify-self-end gap-0.5 pr-0.5 sm:gap-1.5">
        {/* The search, where the full box does not fit. Same action, same
            shortcut - what goes is the width, not the way in. */}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onOpenSearch}
          aria-label="Search leads, jump to…"
          title="Search  ⌘K"
          className={qbee?.open ? undefined : 'xl:hidden'}
        >
          <Search className="size-4" />
        </Button>
        <AlertsBell />
        <WindowChip />
        {action}

        {/* User menu: the account, the theme, and whether this role can
            change anything. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="relative flex items-center gap-2 rounded-md p-1 outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={readOnly ? 'Account menu (read-only access)' : 'Account menu'}
            >
              <Avatar className="size-7">
                <AvatarFallback>{initials(user?.name)}</AvatarFallback>
              </Avatar>
              {readOnly && (
                <span className="absolute -bottom-0.5 -right-0.5 grid size-4 place-items-center rounded-full bg-sky-600 text-white ring-2 ring-sidebar" title="Read-only: your role can view data but not change it.">
                  <Eye className="size-2.5" />
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          {/* DropdownMenuLabel is styled for a SECTION HEADING; reset it for a
              person, or it shouts the name and stretches the address. */}
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="normal-case tracking-normal">
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-[14px] font-semibold text-foreground">
                  {titleCase(user?.name) || 'Account'}
                </span>
                {user?.email && (
                  <span className="truncate text-[13px] font-normal lowercase text-muted-foreground" title={user.email}>
                    {user.email.toLowerCase()}
                  </span>
                )}
                <span className="truncate text-[13px] font-normal text-muted-foreground">
                  {roleLabel ? `Signed in · ${roleLabel}` : 'Signed in'}
                </span>
                {readOnly && (
                  <span className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] font-normal text-sky-700 dark:text-sky-300">
                    <Eye className="size-3.5" /> Read-only: you can view data but not change it.
                  </span>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings?tab=account')}>
              <User />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun /> : <Moon />}
              <span>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-destructive focus:text-destructive">
              <LogOut className="text-destructive" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
